import rulesFile from '@/config/sentry-rules.json'

// Matching is literal (===, startsWith, includes) on purpose: no rule can backtrack or match everything.
// aha-pr-reviewer validates added rules with its own copy of these schema-1 rules; keep the two in step.
const SCHEMA_VERSION = 1
const MAX_RULES = 200
const SAMPLE_RATE = 0.01
const DAY_MS = 24 * 60 * 60 * 1000

const REQUIRED_RULE_KEYS = ['id', 'task_id', 'why', 'match', 'action']
const RULE_KEYS = [...REQUIRED_RULE_KEYS, 'expires']
const MATCH_KEYS = ['message', 'type', 'frames']
const LEVELS = ['fatal', 'error', 'warning', 'info', 'debug']
const DEFAULT_FINGERPRINT = '{{ default }}'

const KEBAB_ID = /^[a-z0-9]+(-[a-z0-9]+)*$/
const TICKET_KEY = /^[A-Z][A-Z0-9]*-\d+$/
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const LETTER = /\p{L}/u

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const hasOnlyKeys = (value, keys) => Object.keys(value).every((key) => keys.includes(key))
const isText = (value, minLength = 1) => typeof value === 'string' && value.length >= minLength

const parseExpiry = (expires) => {
  if (!ISO_DATE.test(expires)) return null
  const start = Date.parse(`${expires}T00:00:00Z`)
  if (Number.isNaN(start) || new Date(start).toISOString().slice(0, 10) !== expires) return null
  return start + DAY_MS
}

const messageError = (message) => {
  if (!isPlainObject(message) || Object.keys(message).length !== 1) return 'match.message needs exactly one of equals / prefix'
  if ('equals' in message) {
    return isText(message.equals, 8) && LETTER.test(message.equals) ? null : 'match.message.equals needs 8+ chars and a letter'
  }
  if ('prefix' in message) return isText(message.prefix, 20) ? null : 'match.message.prefix needs 20+ chars'
  return 'match.message needs exactly one of equals / prefix'
}

const frameEntryError = (entry) => {
  if (!isPlainObject(entry) || Object.keys(entry).length !== 1) return 'a frames entry needs exactly one of function / filenameIncludes'
  if ('function' in entry) return isText(entry.function) ? null : 'frames function must be a non-empty string'
  if ('filenameIncludes' in entry) return isText(entry.filenameIncludes, 6) ? null : 'frames filenameIncludes needs 6+ chars'
  return 'a frames entry needs exactly one of function / filenameIncludes'
}

const matchError = (match) => {
  if (!isPlainObject(match) || !hasOnlyKeys(match, MATCH_KEYS)) return 'match has an unknown key'
  if (!('message' in match)) return 'match.message is required'
  const message = messageError(match.message)
  if (message) return message
  if ('type' in match && !isText(match.type)) return 'match.type must be a non-empty string'
  if ('frames' in match) {
    if (!Array.isArray(match.frames) || match.frames.length < 1 || match.frames.length > 5) return 'match.frames needs 1 to 5 entries'
    return match.frames.map(frameEntryError).find(Boolean) || null
  }
  return null
}

const fingerprintError = (fingerprint) => {
  if (!Array.isArray(fingerprint)) return 'relabel.fingerprint must be an array'
  const literals = fingerprint.filter((part) => part !== DEFAULT_FINGERPRINT)
  if (fingerprint.length - literals.length > 1) return 'relabel.fingerprint may hold {{ default }} once'
  if (literals.length < 1 || literals.length > 4) return 'relabel.fingerprint needs 1 to 4 literal strings'
  const literalOk = (part) => isText(part) && part.length <= 64 && !part.includes('{{')
  return literals.every(literalOk) ? null : 'relabel.fingerprint literals are 1-64 chars, no {{ }}'
}

const actionError = (action) => {
  if (action === 'drop') return null
  if (!isPlainObject(action) || Object.keys(action).length !== 1 || !isPlainObject(action.relabel)) {
    return 'action must be "drop" or { relabel }'
  }
  const { relabel } = action
  if (!hasOnlyKeys(relabel, ['fingerprint', 'level']) || Object.keys(relabel).length < 1) {
    return 'relabel needs fingerprint and/or level, nothing else'
  }
  if ('level' in relabel && !LEVELS.includes(relabel.level)) return 'relabel.level is not a Sentry level'
  return 'fingerprint' in relabel ? fingerprintError(relabel.fingerprint) : null
}

const ruleError = (rule, seenIds) => {
  if (!isPlainObject(rule)) return 'a rule must be an object'
  if (!hasOnlyKeys(rule, RULE_KEYS)) return 'the rule has an unknown key'
  if (!REQUIRED_RULE_KEYS.every((key) => key in rule)) return 'the rule misses a required key'
  if (!isText(rule.id) || !KEBAB_ID.test(rule.id)) return 'id must be kebab-case'
  if (seenIds.has(rule.id)) return 'id is not unique'
  if (!isText(rule.task_id) || !TICKET_KEY.test(rule.task_id)) return 'task_id must be a ticket key'
  if (!isText(rule.why) || rule.why.length > 200) return 'why must be 1-200 chars'
  if ('expires' in rule && (typeof rule.expires !== 'string' || parseExpiry(rule.expires) === null)) {
    return 'expires must be an ISO date (YYYY-MM-DD)'
  }
  return matchError(rule.match) || actionError(rule.action)
}

const warn = (reason) => {
  // eslint-disable-next-line no-console
  console.warn(`[sentry-rules] ${reason}`)
}

const loadRules = (file) => {
  if (!isPlainObject(file) || !hasOnlyKeys(file, ['schema', 'rules']) || file.schema !== SCHEMA_VERSION || !Array.isArray(file.rules)) {
    warn(`expected { "schema": ${SCHEMA_VERSION}, "rules": [...] }; no rules loaded`)
    return []
  }
  if (file.rules.length > MAX_RULES) warn(`only the first ${MAX_RULES} rules are loaded`)

  const seenIds = new Set()
  return file.rules.slice(0, MAX_RULES).reduce((rules, rule, index) => {
    const error = ruleError(rule, seenIds)
    if (error) {
      warn(`rule ${isPlainObject(rule) && typeof rule.id === 'string' ? rule.id : `#${index}`} skipped: ${error}`)
      return rules
    }
    seenIds.add(rule.id)
    rules.push({ ...rule, expiresAt: 'expires' in rule ? parseExpiry(rule.expires) : Infinity })
    return rules
  }, [])
}

const RULES = loadRules(rulesFile)

// beforeSend sees minified frames, so a function is compared by its last segment (HTMLDocument._tapmove → _tapmove).
const frameFunctionName = (frame) => (
  typeof frame?.function === 'string' ? frame.function.split('.').pop() : ''
)

const frameMatches = (frame, entry) => (
  'function' in entry
    ? frameFunctionName(frame) === entry.function
    : typeof frame?.filename === 'string' && frame.filename.includes(entry.filenameIncludes)
)

const valueMatches = (value, { message, type, frames }) => {
  const text = value?.value
  if (typeof text !== 'string') return false
  if ('equals' in message ? text !== message.equals : !text.startsWith(message.prefix)) return false
  if (type !== undefined && value.type !== type) return false
  if (frames === undefined) return true
  const eventFrames = value.stacktrace?.frames
  return Array.isArray(eventFrames) && eventFrames.some((frame) => frames.some((entry) => frameMatches(frame, entry)))
}

const applyRule = (event, rule) => {
  const tags = { ...event.tags, sentry_rule: rule.id }
  if (rule.action === 'drop') {
    return Math.random() < SAMPLE_RATE ? { ...event, level: 'info', tags: { ...tags, sentry_rule_sample: '1' } } : null
  }
  const { fingerprint, level } = rule.action.relabel
  return {
    ...event,
    tags,
    ...(fingerprint && { fingerprint: [...fingerprint] }),
    ...(level && { level }),
  }
}

export const applySentryRules = (event) => {
  try {
    const values = event?.exception?.values
    if (!Array.isArray(values) || RULES.length === 0) return event
    const now = Date.now()
    const rule = RULES.find((candidate) => (
      now < candidate.expiresAt && values.some((value) => valueMatches(value, candidate.match))
    ))
    return rule ? applyRule(event, rule) : event
  } catch {
    return event
  }
}

// unleash.js imports sentry.js, so the flag read is injected from there rather than imported here.
let gate = null

export const setSentryRulesGate = (fn) => {
  gate = typeof fn === 'function' ? fn : null
}

export const sentryRulesGateOn = () => {
  try {
    return gate?.() === true
  } catch {
    return false
  }
}
